import React from 'react';

// Skeleton para las tarjetas KPI
export const SkeletonKPICard = () => (
  <div className="bg-white shadow-lg rounded-xl p-4 border-t-4 border-gray-300 animate-pulse">
    <div className="h-3 w-20 bg-gray-200 rounded mb-2"></div>
    <div className="h-8 w-32 bg-gray-300 rounded"></div>
  </div>
);

// Skeleton para filtros
export const SkeletonFilters = () => (
  <section className="mb-6 bg-white shadow-lg rounded-xl p-6 border-t-4 border-gray-300">
    <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="md:col-span-1">
          <div className="h-4 w-20 bg-gray-200 rounded mb-2 animate-pulse"></div>
          <div className="h-12 w-full bg-gray-200 rounded-lg animate-pulse"></div>
        </div>
      ))}
      <div className="md:col-span-6 flex flex-col sm:flex-row gap-3 md:justify-end">
        <div className="h-12 w-24 bg-gray-300 rounded-lg animate-pulse"></div>
        <div className="h-12 w-16 bg-gray-200 rounded-lg animate-pulse"></div>
      </div>
    </div>
  </section>
);

// Skeleton para la tabla de préstamos
export const SkeletonLoansTable = () => (
  <div className="mt-4 bg-white shadow-lg rounded-xl border-t-4 border-gray-300">
    <div className="overflow-x-auto">
      <table className="min-w-full text-base">
        <thead>
          <tr className="font-semibold text-gray-700 bg-gray-50 border-b border-gray-200">
            {Array.from({ length: 14 }).map((_, i) => (
              <th key={i} className="py-4 px-4 text-center">
                <div className="h-4 w-16 bg-gray-200 rounded mx-auto animate-pulse"></div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 6 }).map((_, rowIndex) => (
            <tr key={rowIndex} className="border-b border-gray-200 last:border-b-0">
              {Array.from({ length: 14 }).map((_, colIndex) => (
                <td key={colIndex} className="py-3 px-4 text-center">
                  {colIndex === 8 ? ( // Columna de cuotas con progreso
                    <div className="space-y-2 animate-pulse">
                      <div className="h-4 w-12 bg-gray-200 rounded mx-auto"></div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="h-2 bg-gray-300 rounded-full w-3/4"></div>
                      </div>
                      <div className="h-3 w-8 bg-gray-200 rounded mx-auto"></div>
                    </div>
                  ) : colIndex === 10 ? ( // Columna de saldo pendiente
                    <div className="space-y-1 animate-pulse">
                      <div className="h-4 w-20 bg-gray-200 rounded mx-auto"></div>
                      <div className="h-3 w-16 bg-gray-200 rounded mx-auto"></div>
                    </div>
                  ) : colIndex >= 11 ? ( // Columnas de badges
                    <div className="inline-block px-2 py-1 rounded-full bg-gray-200 animate-pulse">
                      <div className="h-3 w-16 bg-gray-300 rounded"></div>
                    </div>
                  ) : (
                    <div className="h-4 w-20 bg-gray-200 rounded mx-auto animate-pulse"></div>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="p-4">
      <div className="mx-auto h-1 w-32 bg-gray-200 rounded overflow-hidden">
        <div className="h-full bg-gray-300 animate-pulse"></div>
      </div>
    </div>
  </div>
);

// Skeleton para gráficos
export const SkeletonChart = ({ title = "Cargando gráfico..." }) => (
  <div className="bg-white shadow-lg rounded-xl p-4 border-t-4 border-gray-300 h-full">
    <div className="h-6 w-48 bg-gray-200 rounded mb-4 animate-pulse"></div>
    <div className="h-[340px] bg-gray-100 rounded-lg animate-pulse flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-gray-300 border-t-gray-400 rounded-full mx-auto mb-4 animate-spin"></div>
        <div className="h-4 w-32 bg-gray-300 rounded mx-auto"></div>
      </div>
    </div>
  </div>
);

// Componente mejorado de loading con opciones
export const LoadingSpinner = ({ 
  size = 'medium', 
  message = 'Cargando...', 
  overlay = false,
  color = 'red' 
}) => {
  const sizeClasses = {
    small: 'w-4 h-4',
    medium: 'w-8 h-8',
    large: 'w-12 h-12'
  };

  const colorClasses = {
    red: 'border-red-600',
    blue: 'border-blue-600',
    green: 'border-green-600',
    gray: 'border-gray-600'
  };

  const spinner = (
    <div className="flex flex-col items-center justify-center space-y-3">
      <div className={`
        ${sizeClasses[size]} 
        border-2 ${colorClasses[color]} 
        border-t-transparent 
        rounded-full 
        animate-spin
      `}></div>
      {message && (
        <p className="text-sm text-gray-600 animate-pulse">{message}</p>
      )}
    </div>
  );

  if (overlay) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 shadow-xl">
          {spinner}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center p-8">
      {spinner}
    </div>
  );
};

// Componente para estados vacíos mejorados
export const EmptyState = ({  
  title = "No hay datos", 
  description = "No se encontraron resultados para mostrar",
  actionButton = null 
}) => (
  <div className="bg-white shadow-lg rounded-xl p-8 border-t-4 border-yellow-500">
    <div className="flex flex-col items-center justify-center py-8">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 text-center max-w-md mb-6">{description}</p>
      {actionButton}
    </div>
  </div>
);

// Componente para errores mejorados
export const ErrorState = ({ 
  title = "Algo salió mal", 
  message = "Ha ocurrido un error inesperado",
  onRetry = null 
}) => (
  <div className="bg-white shadow-lg rounded-xl p-8 border-t-4 border-red-600 max-w-md mx-auto">
    <div className="text-center">
      <div className="text-6xl mb-4 animate-bounce"></div>
      <h3 className="text-xl font-semibold text-red-900 mb-2">{title}</h3>
      <p className="text-red-700 mb-6">{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg text-sm font-medium transition transform hover:scale-105"
        >
          Intentar de nuevo
        </button>
      )}
    </div>
  </div>
);

// Componente de notificación toast
export const Toast = ({ 
  type = 'info', 
  title, 
  message, 
  onClose, 
  duration = 5000 
}) => {
  const [visible, setVisible] = React.useState(true);

  React.useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        setVisible(false);
        setTimeout(() => onClose && onClose(), 300);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  if (!visible) return null;

  const typeStyles = {
    success: 'bg-green-600 text-white',
    error: 'bg-red-600 text-white',
    warning: 'bg-yellow-600 text-white',
    info: 'bg-blue-600 text-white'
  };

  return (
    <div className={`fixed top-4 right-4 z-50 max-w-md transform transition-all duration-300 ${
      visible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
    }`}>
      <div className={`${typeStyles[type]} px-4 py-3 rounded-lg shadow-lg flex items-center gap-3`}>
        <div className="flex-1">
          {title && <p className="text-sm font-medium">{title}</p>}
          {message && <p className="text-xs opacity-90">{message}</p>}
        </div>
        <button 
          onClick={() => {
            setVisible(false);
            setTimeout(() => onClose && onClose(), 300);
          }}
          className="hover:bg-black hover:bg-opacity-20 rounded p-1 transition"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

// Componente de carga por pulso para elementos específicos
export const PulseLoader = ({ className = "h-4 w-20 bg-gray-200" }) => (
  <div className={`${className} rounded animate-pulse`}></div>
);

// Skeleton para métricas específicas
export const SkeletonMetrics = () => (
  <div className="space-y-4">
    <div className="flex justify-between items-center">
      <PulseLoader className="h-5 w-32 bg-gray-200" />
      <PulseLoader className="h-8 w-24 bg-gray-300" />
    </div>
    <div className="grid grid-cols-2 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <PulseLoader className="h-3 w-16 bg-gray-200" />
          <PulseLoader className="h-6 w-20 bg-gray-300" />
        </div>
      ))}
    </div>
  </div>
);

// Componente de progreso de carga
export const ProgressBar = ({ progress = 0, message = "Cargando..." }) => (
  <div className="w-full max-w-md mx-auto">
    <div className="flex justify-between text-sm text-gray-600 mb-2">
      <span>{message}</span>
      <span>{Math.round(progress)}%</span>
    </div>
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div 
        className="bg-red-600 h-2 rounded-full transition-all duration-300 ease-out"
        style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
      ></div>
    </div>
  </div>
);

// Esqueleto para elementos de lista
export const SkeletonListItem = ({ lines = 3 }) => (
  <div className="flex items-start space-x-4 p-4 border-b border-gray-200 animate-pulse">
    <div className="w-12 h-12 bg-gray-300 rounded-full flex-shrink-0"></div>
    <div className="flex-1 space-y-2">
      {Array.from({ length: lines }).map((_, i) => (
        <div 
          key={i} 
          className={`h-4 bg-gray-200 rounded ${
            i === 0 ? 'w-3/4' : i === lines - 1 ? 'w-1/2' : 'w-full'
          }`}
        ></div>
      ))}
    </div>
  </div>
);

// Skeleton para búsqueda/filtros avanzados
export const SkeletonSearchFilters = () => (
  <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-4">
    <div className="flex flex-col sm:flex-row gap-4">
      <div className="flex-1">
        <PulseLoader className="h-4 w-20 bg-gray-200 mb-2" />
        <PulseLoader className="h-10 w-full bg-gray-200" />
      </div>
      <div className="sm:w-32">
        <PulseLoader className="h-4 w-16 bg-gray-200 mb-2" />
        <PulseLoader className="h-10 w-full bg-gray-300" />
      </div>
    </div>
    <div className="flex flex-wrap gap-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <PulseLoader key={i} className="h-8 w-16 bg-gray-200 rounded-full" />
      ))}
    </div>
  </div>
);
// Skeleton para la tabla de finanzas (movimientos)
export const SkeletonFinanzasTable = () => (
  <div className="mt-4 bg-white shadow-lg rounded-xl border-t-4 border-gray-300">
    <div className="overflow-x-auto">
      <table className="min-w-full text-base">
        <thead>
          <tr className="font-semibold text-gray-700 bg-gray-50 border-b border-gray-200">
            {Array.from({ length: 6 }).map((_, i) => (
              <th key={i} className="py-4 px-4 text-center">
                <div className="h-4 w-20 bg-gray-200 rounded mx-auto animate-pulse"></div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 6 }).map((_, rowIndex) => (
            <tr key={rowIndex} className="border-b border-gray-200 last:border-b-0">
              {Array.from({ length: 6 }).map((_, colIndex) => (
                <td key={colIndex} className="py-3 px-4 text-center">
                  <div className={`h-4 ${colIndex === 1 ? 'w-56' : 'w-20'} bg-gray-200 rounded mx-auto animate-pulse`}></div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);




